import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import StyledAvailabilityActionButton from 'common/theme/availability/StyledAvailabilityActionButton';
import ConfirmDeleteModal from 'components/shared/modals/ConfirmDeleteModal';
import { getStartEndDatesFromView } from 'common/utils/availability';
import availabilityApi from 'api/graphql/availability';
import availabilitySlice from 'store/modules/availability';
import uiSlice from 'store/modules/ui';
import { createSnack, getUserFriendlyError } from 'common/utils/general';

function AvailabilityDeleteWeekButton({ calendar = null }) {
  const { t } = useTranslation(['buttons', 'notifications', 'availabilities']);
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const userId = useSelector((state) => state.auth.user.id);
  const availabilities = useSelector((state) => state.availability.data.availabilities);

  if (!calendar) return null;

  const dateRange = getStartEndDatesFromView(calendar);
  const hasAvailabilitiesInWeek =
    availabilities &&
    availabilities.some((availability) => {
      const availabilityStart = new Date(availability.start);
      const rangeStart = new Date(dateRange.startDate);
      const rangeEnd = new Date(dateRange.endDate);
      return availabilityStart >= rangeStart && availabilityStart < rangeEnd;
    });

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    if (!isLoading) setOpen(false);
  };

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      await availabilityApi.deleteByDateRange(
        parseInt(userId, 10),
        dateRange.startDate,
        dateRange.endDate
      );

      const snack = createSnack({
        key: 'availability-delete-week-success',
        variant: 'success',
        message: t('availability.deleteWeek', { ns: 'notifications' })
      });

      dispatch(uiSlice.actions.enqueueSnackbar(snack));
      dispatch(availabilitySlice.actions.reset());
      setIsLoading(false);
      setOpen(false);
    } catch (error) {
      const message = getUserFriendlyError(error);
      const snack = createSnack({
        key: 'availability-delete-week-error',
        variant: 'error',
        message
      });

      dispatch(uiSlice.actions.enqueueSnackbar(snack));
      setIsLoading(false);
      setOpen(false);
    }
  };

  return (
    <>
      <StyledAvailabilityActionButton
        onClick={handleOpen}
        disabled={isLoading || !hasAvailabilitiesInWeek}>
        <span>{t('deleteWeek', { ns: 'buttons' })}</span>
      </StyledAvailabilityActionButton>

      <ConfirmDeleteModal
        open={open}
        onClose={handleClose}
        onConfirm={handleConfirm}
        title={t('deleteWeekTitle', {
          ns: 'availabilities',
          defaultValue: '¿Eliminar disponibilidad de esta semana?'
        })}
        message={t('deleteWeekMessage', {
          ns: 'availabilities',
          defaultValue:
            'Esto eliminará las disponibilidades de la semana visible en el calendario que no tengan sesiones programadas. Las sesiones con clientes NO se verán afectadas.'
        })}
        note=""
        confirmLabel={t('delete', { ns: 'buttons' })}
        cancelLabel={t('cancel', { ns: 'buttons' })}
      />
    </>
  );
}

AvailabilityDeleteWeekButton.propTypes = {
  calendar: PropTypes.object
};

export default AvailabilityDeleteWeekButton;
